import React, { useEffect, useMemo, useState } from "react";
import { X, Clock, Plus, Edit2, Trash2, Calendar, Settings } from "lucide-react";
import SlotModal from "./SlotModal";
import axiosInstance from "../../../utils/axiosInstance";
import API_BASE_URL from "../../../services/api";

/* -----------------------------
   Helpers
------------------------------ */
const TZ = "Asia/Kolkata";
const todayIST = () => {
  const now = new Date();
  // simple IST date without dayjs dependency in this file
  // if you already use dayjs in project, you can replace.
  const utcMs = now.getTime() + now.getTimezoneOffset() * 60000;
  const ist = new Date(utcMs + 5.5 * 3600000);
  const y = ist.getFullYear();
  const m = String(ist.getMonth() + 1).padStart(2, "0");
  const d = String(ist.getDate()).padStart(2, "0");
  return `${y}-${m}-${d}`;
};

const formatTime = (time) => {
  if (!time) return "";
  try {
    return new Date(`2000-01-01T${time}`).toLocaleTimeString([], {
      hour: "2-digit",
      minute: "2-digit",
      hour12: true,
    });
  } catch {
    return time;
  }
};

const ruleLabel = (src) => {
  if (src === "date_override") return "Date Override";
  if (src === "day_config") return "Weekly Config";
  return "Default";
};

const DAY_NAMES = ["Sunday", "Monday", "Tuesday", "Wednesday", "Thursday", "Friday", "Saturday"];
const DAY_SHORT = ["Sun", "Mon", "Tue", "Wed", "Thu", "Fri", "Sat"];

/* -----------------------------
   Config Modal (Weekly + Overrides)
------------------------------ */
const SlotCapacityConfigModal = ({ isOpen, onClose, slot, onSaved }) => {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  const [weekView, setWeekView] = useState(
    Array.from({ length: 7 }, (_, i) => ({
      dayOfWeek: i,
      useDefault: true,
      capacity: "",
      isActive: true,
    }))
  );

  const [overrideForm, setOverrideForm] = useState({
    date: todayIST(),
    capacity: "",
    isActive: true,
    note: "",
  });
  const [overrides, setOverrides] = useState([]);

  const loadDayConfigs = async (slotId) => {

    const res = await axiosInstance.get(`${API_BASE_URL}/api/centers/slot/${slotId}/day-config`);
    const serverWeek = res.data?.weekView || [];
    const defaultCap = Number(res.data?.defaultCapacity || 0);

    const next = Array.from({ length: 7 }, (_, i) => {
      const w = serverWeek.find((x) => x.dayOfWeek === i);
      const hasConfig = !!w?.hasConfig;
      return {  
        dayOfWeek: i,
        useDefault: !hasConfig,
        isActive: hasConfig ? !!w.isActive : true,
        capacity: hasConfig ? String(w.capacity ?? "") : String(defaultCap || ""),
      };
    });

    setWeekView(next);
  };

  const loadOverrides = async (slotId) => {
    const res = await axiosInstance.get(`${API_BASE_URL}/api/centers/slot/${slotId}/date-override?upcoming=true`);
    setOverrides(res.data?.overrides || []);
  };

  useEffect(() => {
    if (!isOpen || !slot?.id) return;
    (async () => {
      try {
        setBusy(true);
        setError(null);
        await Promise.all([loadDayConfigs(slot.id), loadOverrides(slot.id)]);
      } catch (e) {
        setError(e?.response?.data?.error || e.message || "Failed to load configs");
      } finally {
        setBusy(false);
      }
    })();
  }, [isOpen, slot?.id]);

  const saveWeekly = async () => {
    if (!slot?.id) return;

    const toDelete = weekView.filter((d) => d.useDefault).map((d) => d.dayOfWeek);

    const toUpsert = weekView
      .filter((d) => !d.useDefault)
      .map((d) => ({
        dayOfWeek: d.dayOfWeek,
        capacity: Number(d.capacity),
        isActive: !!d.isActive,
      }))
      .filter((d) => Number.isFinite(d.capacity));

    try {
      setBusy(true);
      setError(null);

      await Promise.all(
        toDelete.map((dow) =>
          axiosInstance.delete(`${API_BASE_URL}/api/centers/slot/${slot.id}/day-config/${dow}`)
        )
      );

      if (toUpsert.length > 0) {
        await axiosInstance.post(`${API_BASE_URL}/api/centers/slot/${slot.id}/day-config/bulk`, {
          configs: toUpsert,
        });
      }

      await loadDayConfigs(slot.id);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to save weekly capacity");
    } finally {
      setBusy(false);
    }
  };

  const addOverride = async () => {
    if (!slot?.id) return;
    if (!overrideForm.date || overrideForm.capacity === "") {
      setError("Date and capacity are required");
      return;
    }

    try {
      setBusy(true);
      setError(null);

      await axiosInstance.post(`${API_BASE_URL}/api/centers/slot/${slot.id}/date-override`, {
        date: overrideForm.date,
        capacity: Number(overrideForm.capacity),
        isActive: !!overrideForm.isActive,
        note: overrideForm.note || null,
      });

      setOverrideForm({ date: todayIST(), capacity: "", isActive: true, note: "" });
      await loadOverrides(slot.id);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to save override");
    } finally {
      setBusy(false);
    }
  };

  const deleteOverride = async (dateStr) => {
    if (!slot?.id) return;
    if (!window.confirm(`Remove override for ${dateStr}?`)) return;

    try {
      setBusy(true);
      setError(null);
      await axiosInstance.delete(`${API_BASE_URL}/api/centers/slot/${slot.id}/date-override/${encodeURIComponent(dateStr)}`);
      await loadOverrides(slot.id);
      onSaved?.();
    } catch (e) {
      setError(e?.response?.data?.error || e.message || "Failed to delete override");
    } finally {
      setBusy(false);
    }
  };

  if (!isOpen || !slot) return null;

  return (
    <div className="fixed inset-0 z-[60] overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 py-6">
        <div className="fixed inset-0 bg-black/40" onClick={onClose} />
        <div className="relative bg-white w-full max-w-2xl rounded-lg shadow-xl border border-gray-200">
          <div className="px-4 py-3 border-b border-gray-200 flex items-center justify-between">
            <div>
              <h3 className="text-sm font-semibold text-gray-900">Configure Capacity</h3>
              <p className="text-xs text-gray-600 mt-0.5">
                {slot.name || "Slot"} • {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
              </p>
            </div>
            <button onClick={onClose} className="text-gray-400 hover:text-gray-600 p-1 rounded hover:bg-gray-100">
              <X className="w-4 h-4" />
            </button>
          </div>

          {error && (
            <div className="px-4 pt-3">
              <div className="bg-red-50 border border-red-200 rounded p-2 text-xs text-red-700">{error}</div>
            </div>
          )}

          <div className="px-4 py-4 grid grid-cols-1 lg:grid-cols-2 gap-4">
            {/* Weekly capacity */}
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="flex items-center justify-between mb-2">
                <div>
                  <p className="text-xs font-semibold text-gray-900">Weekly Capacity</p>
                  <p className="text-[11px] text-gray-500">Use Default or set custom capacity per day</p>
                </div>
                <button
                  onClick={saveWeekly}
                  disabled={busy}
                  className="text-xs px-2.5 py-1.5 bg-blue-600 text-white rounded hover:bg-blue-700 disabled:opacity-60"
                >
                  {busy ? "Saving..." : "Save"}
                </button>
              </div>

              <div className="space-y-2">
                {weekView.map((d) => (
                  <div key={d.dayOfWeek} className="border border-gray-200 rounded p-2">
                    <div className="flex items-center justify-between">
                      <div className="text-xs font-medium text-gray-900">{DAY_NAMES[d.dayOfWeek]}</div>
                      <label className="flex items-center gap-2 text-xs text-gray-700">
                        <input
                          type="checkbox"
                          checked={!!d.useDefault}
                          onChange={(e) =>
                            setWeekView((p) =>
                              p.map((x) => (x.dayOfWeek === d.dayOfWeek ? { ...x, useDefault: e.target.checked } : x))
                            )
                          }
                        />
                        Use Default
                      </label>
                    </div>

                    {!d.useDefault && (
                      <div className="mt-2 grid grid-cols-2 gap-2 items-end">
                        <div>
                          <label className="block text-[11px] text-gray-500 mb-1">Capacity</label>
                          <input
                            type="number"
                            min="0"
                            value={d.capacity}
                            onChange={(e) =>
                              setWeekView((p) =>
                                p.map((x) => (x.dayOfWeek === d.dayOfWeek ? { ...x, capacity: e.target.value } : x))
                              )
                            }
                            className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
                          />
                        </div>
                        <label className="flex items-center gap-2 text-xs text-gray-700">
                          <input
                            type="checkbox"
                            checked={!!d.isActive}
                            onChange={(e) =>
                              setWeekView((p) =>
                                p.map((x) => (x.dayOfWeek === d.dayOfWeek ? { ...x, isActive: e.target.checked } : x))
                              )
                            }
                          />
                          Active
                        </label>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </div>

            {/* Date overrides */}
            <div className="border border-gray-200 rounded-lg p-3">
              <div className="mb-2">
                <p className="text-xs font-semibold text-gray-900">Date Overrides</p>
                <p className="text-[11px] text-gray-500">Overrides weekly/default capacity for a specific date</p>
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Date</label>
                  <input
                    type="date"
                    value={overrideForm.date}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, date: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
                  />
                </div>
                <div>
                  <label className="block text-[11px] text-gray-500 mb-1">Capacity</label>
                  <input
                    type="number"
                    min="0"
                    value={overrideForm.capacity}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, capacity: e.target.value }))}
                    className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
                  />
                </div>
              </div>

              <div className="flex items-center justify-between mt-2">
                <label className="flex items-center gap-2 text-xs text-gray-700">
                  <input
                    type="checkbox"
                    checked={!!overrideForm.isActive}
                    onChange={(e) => setOverrideForm((p) => ({ ...p, isActive: e.target.checked }))}
                  />
                  Active
                </label>
                <button
                  onClick={addOverride}
                  disabled={busy}
                  className="text-xs px-2.5 py-1.5 bg-gray-900 text-white rounded hover:bg-black disabled:opacity-60"
                >
                  {busy ? "Saving..." : "Add Override"}
                </button>
              </div>

              <div className="mt-3">
                <label className="block text-[11px] text-gray-500 mb-1">Note (optional)</label>
                <input
                  value={overrideForm.note}
                  onChange={(e) => setOverrideForm((p) => ({ ...p, note: e.target.value }))}
                  className="w-full px-2 py-1.5 text-xs border border-gray-300 rounded"
                  placeholder="Holiday / extra capacity..."
                />
              </div>

              <div className="mt-4 border-t border-gray-200 pt-3">
                <p className="text-xs font-medium text-gray-900 mb-2">Upcoming</p>
                <div className="max-h-56 overflow-y-auto border border-gray-200 rounded">
                  <table className="w-full text-xs">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="text-left px-2 py-2 border-b">Date</th>
                        <th className="text-left px-2 py-2 border-b">Cap.</th>
                        <th className="text-left px-2 py-2 border-b">Active</th>
                        <th className="text-right px-2 py-2 border-b">Action</th>
                      </tr>
                    </thead>
                    <tbody>
                      {(overrides || []).map((o) => (
                        <tr key={o.dateLabel} className="hover:bg-gray-50">
                          <td className="px-2 py-2">{o.dateLabel} ({DAY_SHORT[new Date(o.date).getDay()] || o.dayName})</td>
                          <td className="px-2 py-2">{o.capacity}</td>
                          <td className="px-2 py-2">{o.isActive ? "Yes" : "No"}</td>
                          <td className="px-2 py-2 text-right">
                            <button
                              onClick={() => deleteOverride(o.dateLabel)}
                              className="text-red-600 hover:text-red-800"
                            >
                              Delete
                            </button>
                          </td>
                        </tr>
                      ))}
                      {(!overrides || overrides.length === 0) && (
                        <tr>
                          <td colSpan={4} className="px-2 py-3 text-gray-500">
                            No overrides
                          </td>
                        </tr>
                      )}
                    </tbody>
                  </table>
                </div>

                <div className="flex justify-end mt-2">
                  <button
                    onClick={() => loadOverrides(slot.id)}
                    className="text-xs px-2.5 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
                    disabled={busy}
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>
          </div>

          <div className="px-4 py-3 border-t border-gray-200 flex justify-end">
            <button
              onClick={onClose}
              className="text-xs px-3 py-1.5 border border-gray-300 rounded hover:bg-gray-50"
            >
              Close
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

/* -----------------------------
   Main Modal
------------------------------ */
const SlotsManagementModal = ({
  isOpen,
  onClose,
  center,
  slots = [],
  loading = false,
  onSlotsUpdated,
  categories = [],
}) => {
  const [selectedCategoryId, setSelectedCategoryId] = useState("");
  const [slotModalOpen, setSlotModalOpen] = useState(false);
  const [selectedSlot, setSelectedSlot] = useState(null);
  const [mode, setMode] = useState("create");

  const [localSlots, setLocalSlots] = useState(slots);
  const [localLoading, setLocalLoading] = useState(false);

  // ✅ availability date (day-wise)
  const [date, setDate] = useState(todayIST());

  // ✅ config modal
  const [configOpen, setConfigOpen] = useState(false);
  const [configSlot, setConfigSlot] = useState(null);

useEffect(() => {
  try {
    // ✅ Only auto-sync from parent when category is NOT selected
    // If category is selected, we keep the filtered list fetched by API
    if (!selectedCategoryId) {
      setLocalSlots(Array.isArray(slots) ? slots : []);
    }
  } catch (e) {
    console.error("sync localSlots error:", e);
  }
  // eslint-disable-next-line react-hooks/exhaustive-deps
}, [slots, selectedCategoryId]);

  useEffect(() => {
    if (isOpen) {
      setSelectedCategoryId("");
      setSelectedSlot(null);
      setSlotModalOpen(false);
      setMode("create");
     setLocalSlots([]);
      setDate(todayIST());
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, center?.id]);

  if (!isOpen || !center) return null;

  const fetchSlotsByCategory = async (categoryId, dateStr = date) => {
    if (!center?.id) return;

    setLocalLoading(true);
    try {
      const url = categoryId
        ? `${API_BASE_URL}/api/centers/${center.id}/slots?categoryId=${categoryId}&includeGlobal=true&date=${encodeURIComponent(
            dateStr
          )}`
        : `${API_BASE_URL}/api/centers/${center.id}/slots?date=${encodeURIComponent(dateStr)}`;

      const res = await axiosInstance.get(url);
      setLocalSlots(res.data?.slots || res.data?.data || []);
    } catch (error) {
      console.error("Failed to fetch slots:", error);
      setLocalSlots([]);
    } finally {
      setLocalLoading(false);
    }
  };

  const handleOpenCreateSlot = () => {
    if (!selectedCategoryId) return alert("Please select a category first");
    setSelectedSlot(null);
    setMode("create");
    setSlotModalOpen(true);
  };

  const handleOpenEditSlot = (slot) => {
    setSelectedSlot(slot);
    setMode("edit");
    setSlotModalOpen(true);
  };

  const handleDeleteSlot = async (slotId) => {
    if (!window.confirm("Are you sure you want to delete this slot?")) return;
    try {
      await axiosInstance.delete(`${API_BASE_URL}/api/centers/slot/${slotId}`);
      alert("Slot deleted successfully");
      await fetchSlotsByCategory(selectedCategoryId);
      onSlotsUpdated?.();
    } catch (error) {
      console.error("Delete failed:", error);
      alert("Failed to delete slot");
    }
  };

  const shownSlots = localSlots;
  const shownLoading = loading || localLoading;

  return (
    <div className="fixed inset-0 z-50 overflow-y-auto">
      <div className="flex items-center justify-center min-h-screen px-3 pt-3 pb-16 text-center sm:block sm:p-0">
        <div className="fixed inset-0 transition-opacity bg-gray-500 bg-opacity-75" onClick={onClose} />

        <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-6 sm:align-middle sm:max-w-4xl sm:w-full">
          <div className="bg-white px-4 pt-4 pb-3">
            {/* Header */}
            <div className="flex justify-between items-center mb-3">
              <div>
                <h3 className="text-sm font-semibold text-gray-900">Manage Center Slots</h3>
                <p className="text-xs text-gray-600 mt-0.5">
                  {center.name} • {center.city?.name || "No city specified"}
                </p>
              </div>
              <button onClick={onClose} className="text-gray-400 hover:text-gray-500 p-0.5 rounded hover:bg-gray-100">
                <X className="w-4 h-4" />
              </button>
            </div>

            {/* Category + Date */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 mb-3">
              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Select Category (required)</label>
                <select
                  value={selectedCategoryId}
                  onChange={(e) => {
                    const val = e.target.value;
                    setSelectedCategoryId(val);
                    fetchSlotsByCategory(val, date);
                  }}
                  className="w-full px-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                >
                  <option value="">-- Select Category --</option>
                  {categories.map((c) => (
                    <option key={c.id} value={c.id}>
                      {c.name}
                    </option>
                  ))}
                </select>

                {!categories?.length && (
                  <p className="text-xs text-red-600 mt-1">
                    No categories linked to this center. Add center categories first.
                  </p>
                )}
              </div>

              <div>
                <label className="block text-xs font-medium text-gray-700 mb-1">Availability Date</label>
                <div className="flex gap-2">
                  <div className="relative flex-1">
                    <Calendar className="w-4 h-4 text-gray-400 absolute left-2 top-1/2 -translate-y-1/2" />
                    <input
                      type="date"
                      value={date}
                      onChange={(e) => {
                        const d = e.target.value;
                        setDate(d);
                        if (selectedCategoryId) fetchSlotsByCategory(selectedCategoryId, d);
                      }}
                      className="w-full pl-8 pr-2.5 py-1.5 text-xs border border-gray-300 rounded focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                    />
                  </div>
                  <button
                    onClick={() => selectedCategoryId && fetchSlotsByCategory(selectedCategoryId, date)}
                    className="px-3 py-1.5 text-xs border border-gray-300 rounded hover:bg-gray-50"
                  >
                    Refresh
                  </button>
                </div>
              </div>
            </div>

            {/* Stats Cards */}
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3 mb-4">
              <div className="bg-blue-50 p-3 rounded border border-blue-100">
                <div className="flex items-center">
                  <div className="p-1.5 bg-blue-100 rounded">
                    <Clock className="w-4 h-4 text-blue-600" />
                  </div>
                  <div className="ml-2.5">
                    <p className="text-xs font-medium text-blue-600">Total Slots</p>
                    <p className="text-base font-bold text-blue-800">{shownSlots.length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-green-50 p-3 rounded border border-green-100">
                <div className="flex items-center">
                  <div className="p-1.5 bg-green-100 rounded">
                    <Clock className="w-4 h-4 text-green-600" />
                  </div>
                  <div className="ml-2.5">
                    <p className="text-xs font-medium text-green-600">Active Slots</p>
                    <p className="text-base font-bold text-green-800">{shownSlots.filter((s) => s.isActive).length}</p>
                  </div>
                </div>
              </div>

              <div className="bg-purple-50 p-3 rounded border border-purple-100">
                <button
                  onClick={handleOpenCreateSlot}
                  className="w-full h-full flex items-center justify-center hover:bg-purple-100 rounded transition-colors"
                >
                  <div className="flex items-center">
                    <div className="p-1.5 bg-purple-100 rounded">
                      <Plus className="w-4 h-4 text-purple-600" />
                    </div>
                    <div className="ml-2.5 text-left">
                      <p className="text-xs font-medium text-purple-600">Add New Slot</p>
                      <p className="text-xs font-bold text-purple-800">Click here</p>
                    </div>
                  </div>
                </button>
              </div>
            </div>

            {/* Slots Table */}
            {shownLoading ? (
              <div className="flex flex-col items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mb-2" />
                <p className="text-xs text-gray-500">Loading slots...</p>
              </div>
            ) : !selectedCategoryId ? (
              <div className="text-center py-8 border border-dashed border-gray-300 rounded bg-gray-50">
                <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <h4 className="text-sm font-medium text-gray-900 mb-1.5">Select a category to view slots</h4>
                <p className="text-xs text-gray-600">Choose one category above to manage slots for it.</p>
              </div>
            ) : shownSlots.length === 0 ? (
              <div className="text-center py-8 border border-dashed border-gray-300 rounded bg-gray-50">
                <Clock className="w-10 h-10 text-gray-300 mx-auto mb-2" />
                <h4 className="text-sm font-medium text-gray-900 mb-1.5">No slots configured</h4>
                <p className="text-xs text-gray-600 mb-4">Add slots for this category to enable bookings.</p>
                <button
                  onClick={handleOpenCreateSlot}
                  className="px-3 py-1.5 bg-blue-600 hover:bg-blue-700 text-white text-xs font-medium rounded inline-flex items-center"
                >
                  <Plus className="w-3 h-3 mr-1.5" />
                  Add First Slot
                </button>
              </div>
            ) : (
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead>
                    <tr className="bg-gray-50">
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Time Slot
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Default Cap.
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Effective Cap.
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Booked
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Remaining
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Rule
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Status
                      </th>
                      <th className="py-2 px-3 text-left text-xs font-medium text-gray-700 uppercase tracking-wider border-b">
                        Actions
                      </th>
                    </tr>
                  </thead>

                  <tbody className="divide-y divide-gray-200">
                    {shownSlots.map((slot) => (
                      <tr key={slot.id} className="hover:bg-gray-50 transition-colors">
                        <td className="py-2.5 px-3">
                          <div className="flex items-center">
                            <Clock className="w-3.5 h-3.5 text-blue-500 mr-2 flex-shrink-0" />
                            <div>
                              <p className="text-xs font-medium text-gray-900">
                                {formatTime(slot.startTime)} - {formatTime(slot.endTime)}
                              </p>
                              <p className="text-xs text-gray-500">
                                {slot.startTime} - {slot.endTime}
                              </p>
                            </div>
                          </div>
                        </td>

                        <td className="py-2.5 px-3 text-xs text-gray-900">
                          {Number(slot.defaultCapacity ?? slot.capacity ?? 0)}
                        </td>

                        <td className="py-2.5 px-3 text-xs text-gray-900">
                          {Number(slot.effectiveCapacity ?? slot.capacity ?? 0)}
                        </td>

                        <td className="py-2.5 px-3 text-xs text-gray-900">{slot.booked ?? 0}</td>
                        <td className="py-2.5 px-3 text-xs text-gray-900">{slot.remaining ?? 0}</td>

                        <td className="py-2.5 px-3">
                          <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                            {ruleLabel(slot.capacitySource)}
                          </span>
                        </td>

                        <td className="py-2.5 px-3">
                          <span
                            className={`inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium ${
                              slot.isActive ? "bg-green-100 text-green-800" : "bg-red-100 text-red-800"
                            }`}
                          >
                            {slot.isActive ? "Active" : "Inactive"}
                          </span>
                        </td>

                        <td className="py-2.5 px-3">
                          <div className="flex items-center space-x-1.5">
                            <button
                              onClick={() => handleOpenEditSlot(slot)}
                              className="p-1.5 text-gray-400 hover:text-green-600 hover:bg-green-50 rounded transition-colors"
                              title="Edit Slot"
                            >
                              <Edit2 className="w-3.5 h-3.5" />
                            </button>

                            <button
                              onClick={() => {
                                setConfigSlot(slot);
                                setConfigOpen(true);
                              }}
                              className="p-1.5 text-gray-400 hover:text-blue-600 hover:bg-blue-50 rounded transition-colors"
                              title="Weekly/Date Capacity"
                            >
                              <Settings className="w-3.5 h-3.5" />
                            </button>

                            <button
                              onClick={() => handleDeleteSlot(slot.id)}
                              className="p-1.5 text-gray-400 hover:text-red-600 hover:bg-red-50 rounded transition-colors"
                              title="Delete Slot"
                            >
                              <Trash2 className="w-3.5 h-3.5" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}

            {/* Footer */}
            {selectedCategoryId && shownSlots.length > 0 && !shownLoading && (
              <div className="mt-4 pt-3 border-t border-gray-200 flex items-center justify-between">
                <div className="text-xs text-gray-500">
                  Showing <span className="font-medium">{shownSlots.length}</span> slot(s)
                </div>
                <button
                  onClick={handleOpenCreateSlot}
                  className="px-3 py-1.5 text-xs font-medium text-blue-600 hover:text-blue-800 hover:bg-blue-50 rounded transition-colors inline-flex items-center"
                >
                  <Plus className="w-3 h-3 mr-1.5" />
                  Add Another Slot
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Slot create/edit modal */}
      <SlotModal
        isOpen={slotModalOpen}
        onClose={() => {
          setSlotModalOpen(false);
          setSelectedSlot(null);
        }}
        onSave={async () => {
          setSlotModalOpen(false);
          await fetchSlotsByCategory(selectedCategoryId, date);
          onSlotsUpdated?.();
        }}
        center={center}
        slot={selectedSlot}
        mode={mode}
        categoryId={selectedCategoryId}
        title={mode === "edit" ? "Edit Slot" : "Add New Slot"}
      />

      {/* Capacity config modal */}
      <SlotCapacityConfigModal
        isOpen={configOpen}
        slot={configSlot}
        onClose={() => {
          setConfigOpen(false);
          setConfigSlot(null);
        }}
        onSaved={async () => {
          // refresh list for current date
          await fetchSlotsByCategory(selectedCategoryId, date);
          onSlotsUpdated?.();
        }}
      />
    </div>
  );
};

export default SlotsManagementModal;